import { Card, CardBody, CardHeader, Select, SelectItem, Skeleton } from '@heroui/react'
import { BarChart2 } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { useArrayDistribution } from './utils'

// Color palette for different item ranges
const COLORS = {
  '0': '#EF4444', // red-500 for empty arrays
  '1': '#F59E0B', // amber-500
  '2-3': '#10B981', // green-500
  '4-5': '#3B82F6', // blue-500
  '6-10': '#8B5CF6', // violet-500
  '10+': '#EC4899', // pink-500
}

function formatDate(timestamp, interval) {
  const date = new Date(timestamp)
  const options = { month: 'short', day: 'numeric' }

  if (interval === 'hourly') {
    return new Intl.DateTimeFormat('en-US', {
      ...options,
      hour: 'numeric',
      hour12: true,
    }).format(date)
  }

  if (interval === 'weekly') {
    return new Intl.DateTimeFormat('en-US', options).format(date)
  }

  return new Intl.DateTimeFormat('en-US', options).format(date)
}

export default function ArrayDistributionChart({ schemaId, arrayVariables, dateRange }) {
  const [selectedVariable, setSelectedVariable] = useState('')
  const [timeInterval, setTimeInterval] = useState('daily')

  const currentVariable = selectedVariable || arrayVariables?.[0] || ''

  const { data, isLoading } = useArrayDistribution(
    schemaId,
    currentVariable,
    dateRange,
    timeInterval,
  )

  // Transform data for stacked bar chart
  const chartData = useMemo(() => {
    if (!data?.time_series?.length)
      return []

    return data.time_series.map((point) => {
      const dataPoint = {
        timestamp: point.timestamp,
        formattedDate: formatDate(point.timestamp, timeInterval),
      }

      // Initialize all possible ranges with 0
      Object.keys(COLORS).forEach((range) => {
        dataPoint[range] = 0
      })

      // Fill in actual values
      point.distribution?.forEach((dist) => {
        if (dist.item_range in COLORS) {
          dataPoint[dist.item_range] = dist.call_count
        }
      })

      return dataPoint
    })
  }, [data, timeInterval])

  if (!arrayVariables?.length) {
    return null
  }

  if (isLoading) {
    return (
      <Card classNames={{
        base: 'w-full border-2 border-gray-200 shadow-none rounded-none',
      }}
      >
        <CardBody>
          <Skeleton className="h-64 w-full rounded-none" />
        </CardBody>
      </Card>
    )
  }

  if (!chartData.length) {
    return (
      <Card classNames={{
        base: 'w-full border-2 border-gray-200 shadow-none rounded-none',
      }}
      >
        <CardBody>
          <div className="my-24 flex h-full w-full flex-col items-center">
            <h1 className={`
              mb-6 text-2xl font-semibold tracking-tighter text-gray-400
            `}
            >
              No array distribution data yet.
            </h1>
          </div>
        </CardBody>
      </Card>
    )
  }

  return (
    <Card classNames={{
      base: 'w-full border-2 border-gray-200 shadow-none rounded-none',
    }}
    >
      <CardHeader className="flex flex-col gap-3 border-b border-gray-200">
        <div className="flex w-full items-center justify-start gap-4">
          <h4 className="mr-auto font-taurus text-lg font-semibold">Array Item Distribution</h4>
          <BarChart2 size={20} className="text-gray-400" />
        </div>
        <div className="flex w-full items-center gap-4">
          <Select
            selectedKeys={[currentVariable]}
            onSelectionChange={keys => setSelectedVariable(Array.from(keys)[0])}
            className="max-w-xs"
            size="sm"
            variant="bordered"
            radius="none"
            label="Array Variable"
            classNames={{
              trigger: 'border-gray-200',
              value: 'font-mono text-sm',
              label: 'text-xs text-gray-500',
            }}
          >
            {arrayVariables.map(variable => (
              <SelectItem key={variable}>
                {variable}
              </SelectItem>
            ))}
          </Select>
          <Select
            selectedKeys={[timeInterval]}
            onSelectionChange={keys => setTimeInterval(Array.from(keys)[0])}
            className="w-32"
            size="sm"
            variant="bordered"
            radius="none"
            label="Interval"
            classNames={{
              trigger: 'border-gray-200',
              value: 'font-mono text-sm',
              label: 'text-xs text-gray-500',
            }}
          >
            <SelectItem key="hourly">Hourly</SelectItem>
            <SelectItem key="daily">Daily</SelectItem>
            <SelectItem key="weekly">Weekly</SelectItem>
          </Select>
        </div>
      </CardHeader>
      <CardBody>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={chartData}
            margin={{
              top: 10,
              right: 30,
              left: 0,
              bottom: 0,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
            <XAxis
              dataKey="formattedDate"
              tick={{ fontSize: 12, fontFamily: 'ui-monospace, monospace' }}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fontSize: 12, fontFamily: 'ui-monospace, monospace' }}
            />
            <Tooltip
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0)
                  return (
                    <div className={`
                      border-2 border-gray-200 bg-white p-3 shadow-none
                    `}
                    >
                      <p className="mb-2 font-mono text-sm font-medium">{label}</p>
                      <p className="mb-2 font-mono text-xs text-gray-500">
                        Total calls:
                        {' '}
                        {total}
                      </p>
                      {payload
                        .filter(entry => entry.value > 0)
                        .map((entry, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-2 font-mono text-xs"
                          >
                            <div
                              className="h-3 w-3"
                              style={{ backgroundColor: entry.color }}
                            />
                            <span className="text-gray-500">
                              {entry.name === '0' ? 'Empty' : `${entry.name} items`}
                              :
                            </span>
                            <span className="font-medium">
                              {entry.value}
                              {' '}
                              (
                              {((entry.value / total) * 100).toFixed(0)}
                              %)
                            </span>
                          </div>
                        ))}
                    </div>
                  )
                }
                return null
              }}
            />
            <Legend
              wrapperStyle={{
                paddingTop: '20px',
                fontSize: '12px',
                fontFamily: 'ui-monospace, monospace',
              }}
              formatter={(value) => value === '0' ? 'Empty' : `${value} items`}
            />
            {Object.keys(COLORS).map((range) => (
              <Bar
                key={range}
                dataKey={range}
                stackId="a"
                fill={COLORS[range]}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </CardBody>
    </Card>
  )
}